import { Component, inject, input, OnInit, output, signal } from '@angular/core'
import { CommonModule } from '@angular/common'
import { AssetCarouselComponent } from '../asset-carousel/asset-carousel.component'
import { HttpErrorResponse } from '@angular/common/http'
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop'
import { skip, take, combineLatest, filter } from 'rxjs'
import { IAsset } from 'src/app/core/interfaces/asset.interface'
import { AssetService } from 'src/app/core/services/asset.service'
import { LogService } from 'src/app/core/services/log.service'
import { IComponentConfig } from 'src/app/core/interfaces/config.interface'
import { RoutePaths } from 'src/app/core/data/route-paths.data'
import { CurrencyCode } from 'src/app/core/interfaces/currency.interface'
import { TrackingService, UserEvent } from 'src/app/core/services/tracking.service'
import { ColorSchemeDirective } from 'src/app/core/directives/color-scheme.directive'

@Component({
  selector: 'app-featured-carousel',
  template: `
    @if (enabled()) {
      <app-asset-carousel
        [assets]="assets()"
        [viewAllLink]="RoutePaths.Search"
        [viewAllQueryParams]="{ sort: 'featured' }"
        [currencyCode]="currencyCode()"
        [assetCardConfig]="assetCardConfig()"
        [assetCardDefaultImage]="assetCardDefaultImage()"
        (cardClick)="onCardClick($event)"
        (contactClick)="contactClick.emit($event)"
        colorScheme="featuredCarousel.main"
      >
        <ng-template
          #titleTemplate
          i18n="@@featured-carousel.title"
        >Featured Equipment</ng-template>
      </app-asset-carousel>
    }
  `,
  styles: [`
    @use 'src/assets/theme' as theme;
    :host {
      display: block;
    }
  `],
  imports: [
    CommonModule,
    AssetCarouselComponent,
    ColorSchemeDirective,
  ],
})
export class FeaturedCarouselComponent implements OnInit {
  private _assetService = inject(AssetService)
  private _logger = inject(LogService)
  private _trackingService = inject(TrackingService)

  enabled = input(false)
  quantity = input(6)
  currencyCode = input<CurrencyCode>(CurrencyCode.USD)
  assetCardDefaultImage = input<string>('')
  assetCardConfig = input<IComponentConfig>()
  cardClick = output<string>()
  contactClick = output<IAsset>()
  orderIndex = input<number>(0)

  RoutePaths = RoutePaths
  assets = signal<IAsset[]>([])

  constructor() {
    combineLatest([
      toObservable(this.enabled),
      toObservable(this.quantity),
      toObservable(this.orderIndex),
    ]).pipe(
      takeUntilDestroyed(),
      skip(1), // Skip initial emission to avoid duplicate call with ngOnInit
      filter(([enabled]) => enabled),
    ).subscribe(() => {
      this.loadAssets()
    })
  }

  ngOnInit(): void {
    this.loadAssets()
  }

  loadAssets(): void {
    this._assetService
      .getFeaturedAssets(this.quantity())
      .pipe(take(1))
      .subscribe(response => {
        if (response instanceof HttpErrorResponse) {
          this._logger.error('Unable to load assets for featured carousel.  response: ', response)
          return
        }
        this.assets.set(response.data)
      })
  }

  onCardClick($event: string) {
    this.cardClick.emit($event)
    this._trackingService.trackEvent(UserEvent.CarouselClicked, { type: 'featured' })
  }
}
